import _ from 'lodash';
import { Icon, Banner } from '@scale/scaleui';
import { usePinStore } from '../../../ThreadedInteraction/hooks/usePinStore';
import { useMultiChatStoreExtract } from 'react-scale/components/scalers/tasks/MultiChat/hooks/MultiChatStore.hooks';
import { InteractionStepType, IndexedInteractionStepResponse } from '@scale/shared/types/chat';
import { PinnedPrompt } from './PinnedPrompt';
import { PinnedResponse } from './PinnedResponse';

export function PinnedItemsPanel() {
  const pinnedIndexes = usePinStore(state => state.pinnedIndexes);
  const isPinnedIndex = usePinStore(state => state.isPinnedIndex);
  const removePinnedIndex = usePinStore(state => state.removePinnedIndex);
  const isPinnedMessage = usePinStore(state => state.isPinnedMessage);
  const pinnedMessages = usePinStore(state => state.pinnedMessages);
  const removePinnedMessage = usePinStore(state => state.removePinnedMessage);
  const getPinnedMessage = usePinStore(state => state.getPinnedMessage);

  // MultiChat
  const multiChatResponses = useMultiChatStoreExtract(state => state.responses.chatResponses);

  function getMultiChatPinnedItems(): JSX.Element[] {
    const allPinnedItems: JSX.Element[] = [];

    // Iterate through stored multiChatResponses.
    for (const [mcIndex, mcResponses] of Object.entries(multiChatResponses)) {
      // Build pinned items for each chat.
      mcResponses.forEach((response: IndexedInteractionStepResponse, responseIndex: number) => {
        // Check if the prompt is pinned.
        const mcIndexInt = parseInt(mcIndex, 10);
        if (isPinnedMessage(responseIndex, mcIndexInt)) {
          allPinnedItems.push(
            <div
              key={`side-drawer-prompt-${responseIndex}`}
              className="flex align-start gap-4 h-full items-center"
            >
              <div className="flex flex-col items-center">
                <Icon
                  variant="primary"
                  className="border border-neutral-200 shadow-md"
                  icon="exchange"
                  onClick={() => removePinnedMessage(responseIndex, mcIndexInt)}
                  tooltip="Return the item to the main chat view"
                />
              </div>
              <PinnedPrompt
                multiChatIndex={mcIndexInt}
                className="w-full"
                message={getPinnedMessage(responseIndex, mcIndexInt)}
                readOnly={true}
                responseIndex={responseIndex}
                enablePinning={true}
              />
            </div>,
          );
        }

        if (
          isPinnedIndex(responseIndex, mcIndexInt) &&
          response.type !== InteractionStepType.Instruction // Can't get Instruction text from response.
        ) {
          allPinnedItems.push(
            <div
              key={`side-drawer-step-${responseIndex}`}
              className="flex align-start gap-4 h-full items-center"
            >
              <div className="flex flex-col justify-center">
                <Icon
                  variant="primary"
                  className="border border-neutral-200 shadow-md"
                  icon="exchange"
                  onClick={() => {
                    removePinnedIndex(responseIndex, mcIndexInt);
                  }}
                  tooltip="Return the item to the main chat view"
                />
              </div>
              <PinnedResponse
                multiChatIndex={mcIndexInt}
                className="w-full"
                responseIndex={responseIndex}
                response={response}
                isPinnedView={true}
              ></PinnedResponse>
            </div>,
          );
        }
      });
    }

    return allPinnedItems;
  }

  return (
    <div>
      {pinnedIndexes.length === 0 && _.size(pinnedMessages) === 0 && (
        <div className="p-2">
          <Banner
            variant="info"
            title="Pinning step items"
            subtitle="Pin step items to the sidebar by clicking the pin icon to the side of the step. Pinning an item will not make any changes to the chat responses."
            background="neutral"
          />
        </div>
      )}
      <div className="flex flex-col gap-4">{getMultiChatPinnedItems()}</div>
    </div>
  );
}
